import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Tooltip,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import { Close, Visibility, Description, ChatBubbleOutlineOutlined } from '@mui/icons-material';
import { getTeamHealth, getPractitionerCases } from 'api/rest/analytics';
import {
    PractitionerHealth,
    PractitionerCaseItem,
    AnalyticsRoleType,
    Severity,
} from 'hooks/analytics';
import { AnalyticsColors, SectionCard } from '../../components/analyticsUi';
import InfoTooltip from 'components/v2/common/infoTooltip';
import useRoleTypeTabs, { ROLE_TYPE_LABELS } from '../../hooks/useRoleTypeTabs';
import { AnalyticsActions } from '../../hooks/useAnalyticsActions';
import { TODAY_ROLE_HEALTH } from '../../constants/metricDescriptions';

interface RoleHealthSummary {
    roleType: AnalyticsRoleType;
    totalPractitioners: number;
    totalActiveCases: number;
    totalOverdueCases: number;
    totalOnTrackCases: number;
    overdueRate: number;
    health: Severity;
    practitioners: PractitionerHealth[];
}

const computeHealth = (overdueRate: number): Severity => {
    if (overdueRate >= 40) return 'red';
    if (overdueRate >= 20) return 'amber';
    return 'green';
};

const healthColorMap: Record<Severity, string> = {
    red: '#DC2626',
    amber: '#D97706',
    green: '#059669',
};

const healthLabelMap: Record<Severity, string> = {
    red: 'Critical',
    amber: 'Warning',
    green: 'Healthy',
};

const healthBgMap: Record<Severity, string> = {
    red: '#FEE2E2',
    amber: '#FEF3C7',
    green: '#D1FAE5',
};

const healthTextColorMap: Record<Severity, string> = {
    red: '#991B1B',
    amber: '#92400E',
    green: '#065F46',
};

interface RoleHealthSectionProps {
    actions: AnalyticsActions;
    isOwner: boolean;
}

const RoleHealthSection = ({ actions, isOwner }: RoleHealthSectionProps) => {
    const { allowedTabs } = useRoleTypeTabs();
    const [roleSummaries, setRoleSummaries] = useState<RoleHealthSummary[]>([]);
    const [loading, setLoading] = useState<boolean>(allowedTabs.length > 0);
    const [error, setError] = useState<string | null>(null);

    // Drill-down modal state
    const [selectedRole, setSelectedRole] = useState<RoleHealthSummary | null>(null);
    const [roleCases, setRoleCases] = useState<PractitionerCaseItem[]>([]);
    const [casesLoading, setCasesLoading] = useState(false);

    useEffect(() => {
        if (allowedTabs.length === 0) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);

        Promise.all(
            allowedTabs.map((roleType) =>
                getTeamHealth(roleType).then((res) => ({
                    roleType,
                    practitioners: res.data.teamHealth ?? [],
                }))
            )
        )
            .then((results) => {
                if (cancelled) return;
                const summaries: RoleHealthSummary[] = results.map(
                    ({ roleType, practitioners }) => {
                        const totalActiveCases = practitioners.reduce(
                            (sum, p) => sum + p.activeCases,
                            0
                        );
                        const totalOverdueCases = practitioners.reduce(
                            (sum, p) => sum + p.overdueCases,
                            0
                        );
                        const totalOnTrackCases = practitioners.reduce(
                            (sum, p) => sum + p.onTrackCases,
                            0
                        );
                        const overdueRate =
                            totalActiveCases > 0 ? (totalOverdueCases / totalActiveCases) * 100 : 0;
                        return {
                            roleType,
                            totalPractitioners: practitioners.length,
                            totalActiveCases,
                            totalOverdueCases,
                            totalOnTrackCases,
                            overdueRate,
                            health: computeHealth(overdueRate),
                            practitioners,
                        };
                    }
                );
                setRoleSummaries(summaries);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(
                    err?.response?.data?.message || err.message || 'Failed to load Role Health.'
                );
                setRoleSummaries([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [allowedTabs]);

    const handleCardClick = useCallback(async (role: RoleHealthSummary) => {
        setSelectedRole(role);
        setRoleCases([]);
        setCasesLoading(true);
        try {
            const results = await Promise.all(
                role.practitioners.map((p) =>
                    getPractitionerCases(p.practitionerId, role.roleType).then((res) => res.data)
                )
            );
            setRoleCases(results.flat());
        } catch {
            setRoleCases([]);
        } finally {
            setCasesLoading(false);
        }
    }, []);

    const handleCloseModal = () => {
        setSelectedRole(null);
        setRoleCases([]);
    };

    if (allowedTabs.length === 0) return null;

    const totalRed = roleSummaries.filter((r) => r.health === 'red').length;
    const totalAmber = roleSummaries.filter((r) => r.health === 'amber').length;
    const totalGreen = roleSummaries.filter((r) => r.health === 'green').length;

    return (
        <SectionCard>
            {/* Section Title */}
            <Typography
                variant="h2"
                sx={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: AnalyticsColors.textPrimary,
                    m: '0 0 1.5rem 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    letterSpacing: '-0.01em',
                    '&::before': {
                        content: '""',
                        width: '3px',
                        height: '20px',
                        background: AnalyticsColors.primary,
                        borderRadius: '2px',
                        flexShrink: 0,
                    },
                }}
            >
                Team Health
                <InfoTooltip title={TODAY_ROLE_HEALTH} />
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: '2rem' }}>
                    <CircularProgress size={28} />
                </Box>
            ) : error ? (
                <Box
                    sx={{
                        py: '2rem',
                        textAlign: 'center',
                        color: AnalyticsColors.textSecondary,
                        fontSize: '13px',
                    }}
                >
                    {error}
                </Box>
            ) : roleSummaries.length === 0 ? (
                <Box
                    sx={{
                        py: '2rem',
                        textAlign: 'center',
                        color: AnalyticsColors.textSecondary,
                        fontSize: '13px',
                    }}
                >
                    No roles to display.
                </Box>
            ) : (
                <>
                    {/* Summary Chips */}
                    <Box sx={{ display: 'flex', gap: '16px', mb: '16px', flexWrap: 'wrap' }}>
                        {totalRed > 0 && (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    px: '12px',
                                    py: '6px',
                                    background: AnalyticsColors.surfaceBg,
                                    border: `1px solid ${AnalyticsColors.borderLight}`,
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    color: AnalyticsColors.textSecondary,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: '#DC2626',
                                    }}
                                />
                                <Box
                                    component="span"
                                    sx={{ fontWeight: 700, color: AnalyticsColors.textPrimary }}
                                >
                                    {totalRed}
                                </Box>
                                critical
                            </Box>
                        )}
                        {totalAmber > 0 && (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    px: '12px',
                                    py: '6px',
                                    background: AnalyticsColors.surfaceBg,
                                    border: `1px solid ${AnalyticsColors.borderLight}`,
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    color: AnalyticsColors.textSecondary,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: '#D97706',
                                    }}
                                />
                                <Box
                                    component="span"
                                    sx={{ fontWeight: 700, color: AnalyticsColors.textPrimary }}
                                >
                                    {totalAmber}
                                </Box>
                                warning
                            </Box>
                        )}
                        {totalGreen > 0 && (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    px: '12px',
                                    py: '6px',
                                    background: AnalyticsColors.surfaceBg,
                                    border: `1px solid ${AnalyticsColors.borderLight}`,
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    color: AnalyticsColors.textSecondary,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: '#059669',
                                    }}
                                />
                                <Box
                                    component="span"
                                    sx={{ fontWeight: 700, color: AnalyticsColors.textPrimary }}
                                >
                                    {totalGreen}
                                </Box>
                                healthy
                            </Box>
                        )}
                    </Box>

                    {/* Role Cards Grid */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '14px',
                        }}
                    >
                        {roleSummaries.map((role) => {
                            const onTrackPercent =
                                role.totalActiveCases > 0
                                    ? (role.totalOnTrackCases / role.totalActiveCases) * 100
                                    : 100;

                            return (
                                <Box
                                    key={role.roleType}
                                    onClick={() => handleCardClick(role)}
                                    sx={{
                                        background: AnalyticsColors.cardBg,
                                        border: `1px solid ${AnalyticsColors.borderLight}`,
                                        borderRadius: '12px',
                                        p: '1.25rem',
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        '&:hover': {
                                            borderColor: AnalyticsColors.borderMedium,
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.07)',
                                            transform: 'translateY(-1px)',
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: '3px',
                                            background: healthColorMap[role.health],
                                        },
                                    }}
                                >
                                    {/* Card Header */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: '12px',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '8px',
                                                    background: AnalyticsColors.primaryLight,
                                                    color: AnalyticsColors.primary,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    fontSize: '14px',
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {ROLE_TYPE_LABELS[role.roleType].charAt(0)}
                                            </Box>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                        color: AnalyticsColors.textPrimary,
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {ROLE_TYPE_LABELS[role.roleType]}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: AnalyticsColors.textSecondary,
                                                    }}
                                                >
                                                    {role.totalPractitioners} members ·{' '}
                                                    {role.totalActiveCases} active cases
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Health Badge */}
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                px: '10px',
                                                py: '3px',
                                                borderRadius: '6px',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                letterSpacing: '0.02em',
                                                background: healthBgMap[role.health],
                                                color: healthTextColorMap[role.health],
                                                whiteSpace: 'nowrap',
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    background: healthTextColorMap[role.health],
                                                    mr: '5px',
                                                    flexShrink: 0,
                                                }}
                                            />
                                            {healthLabelMap[role.health]}
                                        </Box>
                                    </Box>

                                    {/* Progress Bar */}
                                    <Tooltip
                                        title={`${role.totalOnTrackCases} on track · ${role.totalOverdueCases} overdue`}
                                        arrow
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: 6,
                                                background: '#F3F4F6',
                                                borderRadius: '3px',
                                                overflow: 'hidden',
                                                my: '12px',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: '100%',
                                                    width: `${onTrackPercent}%`,
                                                    background: healthColorMap[role.health],
                                                    borderRadius: '3px',
                                                    transition: 'width 0.4s ease',
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>

                                    {/* Footer Stats */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                color: AnalyticsColors.textSecondary,
                                            }}
                                        >
                                            {role.totalOverdueCases} of {role.totalActiveCases}{' '}
                                            overdue
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: 700,
                                                color: healthColorMap[role.health],
                                            }}
                                        >
                                            {role.overdueRate.toFixed(0)}%
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}

            {/* Drill-Down Modal */}
            {selectedRole && (
                <Dialog open onClose={handleCloseModal} fullWidth maxWidth="md">
                    <DialogTitle sx={{ pb: 0.5 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box>
                                <Typography
                                    component="span"
                                    sx={{ fontWeight: 700, fontSize: '16px' }}
                                >
                                    {ROLE_TYPE_LABELS[selectedRole.roleType]} Cases
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '13px',
                                        color: AnalyticsColors.textSecondary,
                                        fontWeight: 400,
                                        mt: '2px',
                                    }}
                                >
                                    {roleCases.length} active cases ·{' '}
                                    {selectedRole.totalOverdueCases} overdue
                                </Typography>
                            </Box>
                            <Tooltip title="Close" arrow>
                                <IconButton onClick={handleCloseModal} size="small">
                                    <Close sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ px: 2, pb: 2 }}>
                        {casesLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: '2rem' }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : roleCases.length === 0 ? (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: '2rem',
                                    color: AnalyticsColors.textTertiary,
                                    fontSize: '13px',
                                }}
                            >
                                No cases to show
                            </Box>
                        ) : (
                            <Box
                                component="table"
                                sx={{ width: '100%', borderCollapse: 'collapse' }}
                            >
                                <Box component="thead">
                                    <Box component="tr">
                                        {[
                                            'Client',
                                            'Program',
                                            'Days Active',
                                            'Status',
                                            'Actions',
                                        ].map((header, i) => (
                                            <Box
                                                component="th"
                                                key={header}
                                                sx={{
                                                    textAlign:
                                                        i === 2 || i === 4 ? 'right' : 'left',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    color: AnalyticsColors.textTertiary,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    p: '0.6rem 0.75rem',
                                                    borderBottom: `2px solid ${AnalyticsColors.borderLight}`,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {header}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                                <Box component="tbody">
                                    {roleCases.map((c, idx) => (
                                        <Box
                                            component="tr"
                                            key={`${c.applicationId}-${idx}`}
                                            sx={{
                                                transition: 'background 0.1s',
                                                '&:hover': { background: '#FAFAFE' },
                                                '&:last-child td': { borderBottom: 'none' },
                                            }}
                                        >
                                            {/* Client */}
                                            <Box
                                                component="td"
                                                sx={{
                                                    fontSize: '13px',
                                                    color: AnalyticsColors.primary,
                                                    p: '0.65rem 0.75rem',
                                                    borderBottom: '1px solid #F3F4F6',
                                                    verticalAlign: 'middle',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    '&:hover': { textDecoration: 'underline' },
                                                }}
                                                onClick={() => actions.navigateToClient(c.clientId)}
                                            >
                                                {c.candidateName}
                                            </Box>
                                            {/* Program */}
                                            <Box
                                                component="td"
                                                sx={{
                                                    fontSize: '13px',
                                                    color: AnalyticsColors.textPrimary,
                                                    p: '0.65rem 0.75rem',
                                                    borderBottom: '1px solid #F3F4F6',
                                                    verticalAlign: 'middle',
                                                }}
                                            >
                                                {c.programName}
                                            </Box>
                                            {/* Days Active */}
                                            <Box
                                                component="td"
                                                sx={{
                                                    fontSize: '13px',
                                                    color: AnalyticsColors.textPrimary,
                                                    p: '0.65rem 0.75rem',
                                                    borderBottom: '1px solid #F3F4F6',
                                                    verticalAlign: 'middle',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}
                                            >
                                                {c.daysActive}d
                                            </Box>
                                            {/* Status */}
                                            <Box
                                                component="td"
                                                sx={{
                                                    fontSize: '13px',
                                                    color: AnalyticsColors.textPrimary,
                                                    p: '0.65rem 0.75rem',
                                                    borderBottom: '1px solid #F3F4F6',
                                                    verticalAlign: 'middle',
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: '11px',
                                                        fontWeight: 500,
                                                        px: '8px',
                                                        py: '2px',
                                                        borderRadius: '4px',
                                                        background:
                                                            c.status.includes('Pending') ||
                                                            c.status.includes('Required')
                                                                ? '#FEF3C7'
                                                                : '#F0FDF4',
                                                        color:
                                                            c.status.includes('Pending') ||
                                                            c.status.includes('Required')
                                                                ? '#92400E'
                                                                : '#166534',
                                                    }}
                                                >
                                                    {c.status}
                                                </Box>
                                            </Box>
                                            {/* Actions */}
                                            <Box
                                                component="td"
                                                sx={{
                                                    p: '0.65rem 0.75rem',
                                                    borderBottom: '1px solid #F3F4F6',
                                                    verticalAlign: 'middle',
                                                    textAlign: 'right',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <Tooltip title="View Case" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            actions.navigateToClient(c.clientId)
                                                        }
                                                        sx={{
                                                            color: AnalyticsColors.textTertiary,
                                                            '&:hover': {
                                                                color: AnalyticsColors.primary,
                                                                background:
                                                                    AnalyticsColors.primaryLight,
                                                            },
                                                        }}
                                                    >
                                                        <Visibility sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="View Documents" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            actions.navigateToDocuments(
                                                                c.clientId,
                                                                c.applicationId
                                                            )
                                                        }
                                                        sx={{
                                                            color: AnalyticsColors.textTertiary,
                                                            '&:hover': {
                                                                color: AnalyticsColors.primary,
                                                                background:
                                                                    AnalyticsColors.primaryLight,
                                                            },
                                                        }}
                                                    >
                                                        <Description sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {isOwner && (
                                                    <Tooltip title="Message Client" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                actions.openMessageComposer(
                                                                    0,
                                                                    c.candidateName
                                                                )
                                                            }
                                                            sx={{
                                                                color: AnalyticsColors.textTertiary,
                                                                '&:hover': {
                                                                    color: AnalyticsColors.primary,
                                                                    background:
                                                                        AnalyticsColors.primaryLight,
                                                                },
                                                            }}
                                                        >
                                                            <ChatBubbleOutlineOutlined
                                                                sx={{ fontSize: 16 }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </SectionCard>
    );
};

export default RoleHealthSection;
